import "@/App.css";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, RefreshCw, Trash2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cv_admin_token";

export default function WifiAdmin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [pw, setPw] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const login = async () => {
    setLoginErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch { setLoginErr("Invalid password"); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/wifi/leads`, {
        headers: { "X-Admin-Token": token },
      });
      setLeads(data.leads || []);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const download = async (fmt) => {
    const res = await axios.get(`${API}/admin/wifi/export?format=${fmt}`, {
      headers: { "X-Admin-Token": token },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cinevibe-wifi-leads.${fmt === "csv" ? "csv" : "xlsx"}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await axios.delete(`${API}/admin/wifi/leads/${id}`, { headers: { "X-Admin-Token": token } });
    load();
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#070401] text-[#f4d089] flex items-center justify-center px-5">
        <div className="w-full max-w-sm rounded-xl border border-[#3a2a14]/70 bg-[#120c06] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5" />
            <h1 className="text-xl font-semibold">Wi-Fi Admin</h1>
          </div>
          <input
            data-testid="wifi-admin-pw"
            type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder="Admin password"
            className="w-full px-4 py-3 rounded-md bg-white/95 text-[#1a0f06] outline-none"
            onKeyDown={(e) => e.key === "Enter" && login()}
          />
          {loginErr && <p className="text-red-300 text-sm mt-2">{loginErr}</p>}
          <button
            data-testid="wifi-admin-login-btn"
            onClick={login}
            className="mt-4 w-full px-4 py-3 rounded-md font-semibold bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06]"
          >Login</button>
        </div>
      </div>
    );
  }

  const filtered = leads.filter(l =>
    !search || (l.phone || "").includes(search) || (l.device_type || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#070401] text-[#f4d089] px-5 sm:px-8 py-6">
      <div className="max-w-[1280px] mx-auto">
        <div className="flex flex-wrap items-center gap-3 justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Free Wi-Fi — Customer Database</h1>
            <p className="text-[#cbbf9f] text-sm">Total leads: {leads.length}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="wifi-admin-refresh" onClick={load} className="px-4 py-2 rounded-md bg-white/10 hover:bg-white/15 flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button data-testid="wifi-admin-csv" onClick={() => download("csv")} className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-2">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button data-testid="wifi-admin-xlsx" onClick={() => download("xlsx")} className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-2">
              <Download className="w-4 h-4" /> Excel
            </button>
            <button onClick={logout} className="px-4 py-2 rounded-md bg-white/10 hover:bg-white/15">Logout</button>
          </div>
        </div>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by phone or device..."
          className="w-full sm:w-80 mb-4 px-4 py-2 rounded-md bg-white/95 text-[#1a0f06] outline-none"
        />

        <div className="overflow-x-auto rounded-xl border border-[#3a2a14]/70">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[#f4d089]">
              <tr>
                {["Mobile", "Date", "Time", "Device", "IP", "Consent", "WhatsApp", "Instagram", "Connected", ""].map(h => (
                  <th key={h} className="text-left px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-3 py-6 text-center text-[#cbbf9f]">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-[#cbbf9f]">No leads yet.</td></tr>}
              {filtered.map((l) => {
  const istParts = (() => {
    if (!l.created_at) return ["", ""];
    // created_at is UTC ISO -> convert to IST (Asia/Kolkata)
    const dt = new Date(l.created_at.endsWith("Z") || l.created_at.includes("+")
      ? l.created_at : l.created_at + "Z");
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }).formatToParts(dt);
    const get = (t) => fmt.find(p => p.type === t)?.value || "";
    return [`${get("year")}-${get("month")}-${get("day")}`, `${get("hour")}:${get("minute")}:${get("second")}`];
  })();
  const [d, t] = istParts;
                const Yn = (v) => <span className={v ? "text-emerald-300" : "text-red-300"}>{v ? "Yes" : "No"}</span>;
                return (
                  <tr key={l.id} className="border-t border-white/5 text-[#e8dcb8]">
                    <td className="px-3 py-2 font-mono">{l.country_code} {l.phone}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{d}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{t}</td>
                    <td className="px-3 py-2">{l.device_type}</td>
                    <td className="px-3 py-2">{l.ip}</td>
                    <td className="px-3 py-2">{Yn(l.consent)}</td>
                    <td className="px-3 py-2">{Yn(l.whatsapp_clicked)}</td>
                    <td className="px-3 py-2">{Yn(l.instagram_clicked)}</td>
                    <td className="px-3 py-2">{Yn(l.connected)}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => del(l.id)} className="text-red-300 hover:text-red-200" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
